import {
  AfterContentChecked,
  AfterViewInit,
  ApplicationRef,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import { AgregationService } from '@ispm-client/modules/aggregator/services';
import { tooltipServiceProvider } from '@ispm/shared/services/tooltip.service.provider';
import { TooltipService } from '@ispm/shared/services/tooltip.service';
import { Observable, Subject, Subscription, fromEvent, merge } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { cloneDeep, find, get, has, isEmpty } from 'lodash';
import {
  IDynamicFormItemTooltipPattern,
  IDynamicFormItemTooltipWidget
} from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { TranslateService } from '@ngx-translate/core';
import { IspmTooltipComponent } from '@ispm/shared/components/tooltip/tooltip.component';
import { WidgetItemComponent } from '@ispm/dynamic-form/components/widget-item/widget-item.component';
import { InterpolateService } from '@ispm/shared/services/interpolate.service';
import { FROM_STORE } from '@ispm/shared/constants/data-sources';
import { SelectorsMapping } from '@ispm/shared/utils/Selectors';
import { select, Store } from '@ngrx/store';
import { GetExecutionPathAction } from '@ispm-client/modules/pathway-selections/store/actions/pathway-selections.actions';

@Directive({
  selector: '[genTooltip]',
  providers: [tooltipServiceProvider],
  exportAs: 'TooltipDirective'
})
export class TooltipDirective implements OnInit, OnChanges, AfterViewInit, OnDestroy, AfterContentChecked {
  @Input()
  set tooltipConfiguration(tooltipConfiguration: IDynamicFormItemTooltipPattern | IDynamicFormItemTooltipWidget) {
    this._tooltipConfiguration = tooltipConfiguration;
    // this.initTooltipData(); When this runs formId is undefined
  }

  get tooltipConfiguration(): IDynamicFormItemTooltipPattern | IDynamicFormItemTooltipWidget {
    return this._tooltipConfiguration;
  }

  @Input()
  set tooltipHtml(tooltip: any) {
    this._tooltipHtml = tooltip;
    if (this.enabled) {
      this.forceUpdate = true;
      this.showTooltip();
    }
  }

  get tooltipHtml() {
    return this._tooltipHtml;
  }

  @Input()
  set displayStatus(displayStatus: any) {
    if (displayStatus) {
      this.showTooltip();
    }
  }
  private unsubscribe$ = new Subject<void>();
  private subscription$: Subscription;

  constructor(private element: ElementRef,
              private _zone: NgZone,
              private _changeDetector: ChangeDetectorRef,
              private _agregator: AgregationService,
              private _translate: TranslateService,
              private _tooltipService: TooltipService,
              private resolver: ComponentFactoryResolver,
              private viewContainerRef: ViewContainerRef,
              private renderer: Renderer2,
              private applicationReference: ApplicationRef,
              private injector: Injector,
              private store$: Store<any>) {
  }

  static readonly ARROW_OFFSET_PX = 10;
  static readonly ENABLED_TOOLTIP_CLASS: string = 'tooltip-enabled';
  enabled: boolean;
  /**
   *  Disabling tooltip by default
   */
  @Input() tooltipShow?: boolean = false;
  _tooltipText: string;

  get tooltipText(): string {
    return this._tooltipText;
  }

  @Input() set tooltipText(text: string) {
    if (text !== undefined) {
      this._tooltipText = text;
    }
  }

  private _tooltipConfiguration: IDynamicFormItemTooltipPattern | IDynamicFormItemTooltipWidget;
  @Input() tooltipAdditionalData: any;
  @Input() formId: string;
  @Input() entryRef: ViewContainerRef;
  @Input() tooltipTitle: string = '';
  @Input() tooltipPosition?: string | tooltipPositions = 'RIGHT';
  @Input() tooltipYOffset?: number;
  @Input() initialDisplayDelay: number = 3000;
  @Input() appendToBody: boolean = false;
  @Input() initialStatus: boolean;
  @Input() updateMaxToolTipWidthRelatively: boolean = false;

  @Input()
  set tooltipData(data: any) {
    if (this.tooltipData !== data && !isEmpty(data)) {
      this._tooltipData = data;
      this.setTooltipText();
    }
  }

  get tooltipData(): any {
    return this._tooltipData;
  }

  // indicate if to use the native-element's offset position instead of the BoundingClientRect structure
  @Input() useElementOffsetPos: boolean = false;
  @Input() maxTooltipHeight?: number;
  @Input() maxTooltipWidth?: number;
  @Input() minTooltipHeight?: number;
  @Input() fixedTooltipWidth?: number;
  @Input() maxNumberOfLinesInTooltip = 5;
  @Input() fontsize?: string;

  private arrowPosition: any = { };
  private _template: any = document.createElement('section');
  private templateInit: boolean = false;
  private _tooltipData: any;
  private tooltipForm: any;
  private _tooltipHtml: any;
  private data$: Observable<any>;
  private forceUpdate: boolean = false;
  private componentRef: ComponentRef<IspmTooltipComponent>;
  private destroyed$: Subject<any> = new Subject();
  private _tooltipBodyElement: string = 'span';
  private tooltipClass: string = '';
  private usingIE: boolean = false;
  private usingChrome: boolean = false;

  mouseShowTooltip($event) {
    $event.stopPropagation();
    this.showTooltip($event);
  }
  showTooltip($event = null) {
    if (this.preChecks()) {
      if (this.componentRef) {
        return;
      }
      const factory = this.resolver.resolveComponentFactory(IspmTooltipComponent);
      const injector = Injector.create(
        {
          providers: [{
            provide: 'tooltipElementReference',
            useValue: {
              host: this.element.nativeElement
            },
            deps: []
          }]
        }
      );
      this.componentRef = this.viewContainerRef.createComponent(factory, 0, injector, this.generateComponentContent());
      this._changeDetector.detectChanges();
      this.setComponentData();
    } else {
      if ($event === null && this.tooltipAdditionalData) {
        setTimeout(() => {
          this.hideTooltip();
          if (this._changeDetector !== null && this._changeDetector !== undefined && !this._changeDetector['destroyed']) {
            this._changeDetector.detectChanges();
          }
        }, this.initialDisplayDelay);
      }
      const elementBoundingRect = this.element.nativeElement.getBoundingClientRect();
      this.arrowPosition = {
        x: elementBoundingRect.left + elementBoundingRect.width / 2 - TooltipDirective.ARROW_OFFSET_PX / 2,
        y: elementBoundingRect.top + elementBoundingRect.height / 2 - TooltipDirective.ARROW_OFFSET_PX / 2
      };

      if (!(this.tooltipShow && (this.tooltipText || this.tooltipTitle || this.tooltipHtml))) {
        return;
      }
      this.enabled = true;
      this.executeAction();
      this.addTooltip($event);
      if (this.appendToBody) {
        this._template.classList.add(TooltipDirective.ENABLED_TOOLTIP_CLASS);
      }
    }
  }

  hideTooltip() {
    this.destroyComponentInstance();
    this.enabled = false;
    this.executeAction();
    if (this.appendToBody) {
      this._template.classList.remove(TooltipDirective.ENABLED_TOOLTIP_CLASS);
    }
  }

  ngOnInit() {
    if (!(this.tooltipConfiguration && this.tooltipConfiguration.componentBasedTooltip)) {
      this._template.classList.add('tooltip-generic');
      this.initTooltipData();
    }
    this.usingIE = (navigator.userAgent.indexOf('Trident') !== -1);
    this.usingChrome = (navigator.userAgent.indexOf('Chrome') !== -1);
    this.enablingEventListeners();
  }

  private executeAction(): void {
    const action = this.enabled ? 'addClass' : 'removeClass';
    this.renderer[action](this.element.nativeElement, TooltipDirective.ENABLED_TOOLTIP_CLASS);
  }

  enablingEventListeners(){
    const mouseover$ = fromEvent(this.element.nativeElement, 'mouseover');
    mouseover$.pipe(takeUntil(this.unsubscribe$)).subscribe((event: MouseEvent) => {
      this.mouseShowTooltip(event);
    });
    const focusin$ = fromEvent(this.element.nativeElement, 'focusin');
    focusin$.pipe(takeUntil(this.unsubscribe$)).subscribe((event: MouseEvent) => {
      this.mouseShowTooltip(event);
    });
    const mouseout$ = fromEvent(this.element.nativeElement, 'mouseout');
    const focusout$ = fromEvent(this.element.nativeElement, 'focusout');
    const click$ = fromEvent(this.element.nativeElement, 'click');
    merge(mouseout$, focusout$, click$)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((event: MouseEvent) => {
        this.hideTooltip();
      });
    const resize$ = fromEvent(this.element.nativeElement, 'window:resize');
    resize$.pipe(takeUntil(this.unsubscribe$)).subscribe((event: MouseEvent) => {
      this.removeTooltip();
    });
  }
  initTooltipData() {
    if (this._tooltipConfiguration) {
      this.appendToBody = this._tooltipConfiguration['appendToBody'];
      this.displayStatus = this._tooltipConfiguration['displayStatus'];
      this._tooltipBodyElement = this._tooltipConfiguration.tooltipBodyElement || 'span';
      if (has(this._tooltipConfiguration, ['tooltipPosition'])) {
        this.tooltipPosition = this._tooltipConfiguration.tooltipPosition;
      }
      if (has(this._tooltipConfiguration, ['tooltipClass'])) {
        this.tooltipClass = this._tooltipConfiguration.tooltipClass;
        this._template.classList.add(this.tooltipClass);
      }
      this.tooltipTitle = this._tooltipConfiguration.tooltipTitle || this.tooltipTitle;
      this.tooltipForm = {
        'formId': this.formId,
        'identifiers': [this._tooltipConfiguration.identifier]
      };
      if (this._tooltipConfiguration.identifier) {
        this._agregator.getIdentifierData(this.tooltipForm).pipe(
          switchMap(
            (data) => {
              this._tooltipData = cloneDeep(data);
              return this._tooltipService.getTooltipContent(data, this._tooltipConfiguration, this.entryRef);
            }
          )).subscribe(html => {
          this.tooltipHtml = html;
        });
      } else if (this.tooltipAdditionalData) {
        this.data$ = this._tooltipService.getTooltipContent(this.tooltipAdditionalData, this.tooltipConfiguration, this.entryRef);

        if (this.data$ !== undefined) {
          this.subscription$ = this.data$.subscribe(html => {
            this.tooltipHtml = html;

          });
        }
      } else if (this.tooltipConfiguration.tooltipBodySource === FROM_STORE) {
        this.store$.pipe(
          select(SelectorsMapping[this.tooltipConfiguration.storeSelector]),
          takeUntil(this.destroyed$.asObservable())
        ).subscribe(tpNote => {
          this.tooltipHtml = tpNote && tpNote.trim() !== '' ? tpNote : '<p>No treatment selection note present</p>';
        });
        this.store$.dispatch(new GetExecutionPathAction({ }));
      } else {
        this._tooltipService.getTooltipContent(null, this._tooltipConfiguration, this.entryRef).subscribe(html => {
            this.tooltipHtml = html;
        });
      }
    } else if (!!this.tooltipHtml && !!this.tooltipText) {
      console.error(`Genomics Tooltip only supports html or text, please remove one of those attributes. text: ${this.tooltipText} html:${this.tooltipHtml} `);
      return;
    }
    this._template.classList.add('tooltip-generic');
    this._template.setAttribute('Auto-Tooltip-Value', this.tooltipText || this.tooltipHtml);
  }

  ngAfterViewInit(): void {
    if (this.initialStatus && !(this.tooltipConfiguration && this.tooltipConfiguration.componentBasedTooltip)) {
      this.showTooltip();
      this._changeDetector.detectChanges();
      if (this.initialDisplayDelay !== null) {
        this._zone.runOutsideAngular(() => {
          setTimeout(() => {
            this.hideTooltip();
            this._changeDetector.detectChanges();
          }, this.initialDisplayDelay);
        });
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!(this.tooltipConfiguration && this.tooltipConfiguration.componentBasedTooltip)) {
      if ((changes.tooltipHtml && !changes.tooltipHtml.firstChange && changes.tooltipHtml.previousValue !== changes.tooltipHtml.currentValue)
        || (changes.tooltipText && !changes.tooltipText.firstChange && changes.tooltipText.previousValue !== changes.tooltipText.currentValue)) {
        this.removeTooltip();
      } else if (changes.tooltipPosition && !changes.tooltipPosition.firstChange && changes.tooltipPosition.previousValue !== changes.tooltipPosition.currentValue) {
        this.setPosition(changes.tooltipPosition.previousValue);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.subscription$) {
      this.subscription$.unsubscribe();
    }
    this.destroyComponentInstance();
    this._template.remove();
    this._template = null;
    this.unsubscribe$.next();
    this.viewContainerRef.clear();
    this.unsubscribe$.complete();
    // this._changeDetector.detach();
      // this._tooltipService.destroy();
  }

  private generateComponentContent() {
    if (this.tooltipText && typeof this.tooltipText === 'string') {
      const element = this.renderer.createText(this.tooltipText);
      return [[element]];
    } else if (this.tooltipHtml instanceof TemplateRef) {
      const viewRef = this.tooltipHtml.createEmbeddedView({ });
      return [viewRef.rootNodes];
    } else if (this._tooltipConfiguration && this._tooltipConfiguration.payload) {
      const factory = this.resolver.resolveComponentFactory(WidgetItemComponent);
      const widgetComponentRef = factory.create(this.injector);
      widgetComponentRef.instance.componentToLoad = this._tooltipConfiguration.payload.componentToLoad;
      widgetComponentRef.instance.item = this._tooltipConfiguration.payload;
      widgetComponentRef.instance.value = this.tooltipData;
      this.applicationReference.attachView(widgetComponentRef.hostView);
      return [[widgetComponentRef.location.nativeElement]];
    }
  }

  destroyComponentInstance() {
    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }

  private setComponentData() {
    if (typeof this.tooltipPosition === 'string') {
      this.componentRef.instance.tooltipPosition = tooltipPositions[(this.tooltipPosition).toUpperCase()];
    } else if (this._tooltipConfiguration && typeof this._tooltipConfiguration.tooltipPosition === 'string') {
      this.componentRef.instance.tooltipPosition = tooltipPositions[(<string> this._tooltipConfiguration.tooltipPosition).toUpperCase()];
    } else {
      this.componentRef.instance.tooltipPosition = tooltipPositions[this.tooltipPosition];
    }
    if (this._tooltipConfiguration) {
      this.componentRef.instance.tooltipConfiguration = this._tooltipConfiguration;
    }
  }

  private setTemplate($event) {
    if (this.templateInit && !this.forceUpdate) {
      return;
    }
    this._template.innerHTML = '';
    this.templateInit = true;
    const arrow = document.createElement('span');
    arrow.classList.add('tooltip-arrow');
    this._template.appendChild(arrow);
    const body = document.createElement(this._tooltipBodyElement);
    body.classList.add('tooltip-content');
    if (this.tooltipText) {
      const text = document.createTextNode(this._translate.instant(this.tooltipText));
      body.appendChild(text);
    } else if (find(this._tooltipConfiguration, ['attributes.translate'])) {
        body.innerHTML = this._tooltipConfiguration.attributes.translate
                       ? this._translate.instant(this.tooltipHtml)
                       : this.tooltipHtml;
    } else {
      body.innerHTML = this._translate.instant(this.tooltipHtml);
    }
    if (this._tooltipConfiguration) {
      if (this._tooltipConfiguration.tooltipTitle && !(this._tooltipConfiguration.payload && this._tooltipConfiguration.payload.tableTitle)) {
        const label = document.createElement('div');
        label.textContent = this._tooltipConfiguration.tooltipTitle;
        label.classList.add('tooltip-title');
        label.classList.add(this.tooltipClass);
        this._template.appendChild(label);
      }
      if (has(this._tooltipConfiguration, ['pattern'])) {
        body.classList.add('pattern-tooltip-content');
      }
      body.classList.add(this.tooltipClass);
      const tooltipStyle = this._tooltipConfiguration.tooltipStyle;
      if (tooltipStyle) {
        if (this.usingIE) {
          this._template.style.setAttribute('background-color', tooltipStyle.backgroundColor);
          this._template.style.setAttribute('color', tooltipStyle.color);
          this._template.style.setAttribute('border-color', tooltipStyle.backgroundColor);
          console.log('template style: ', this._template.style);
          if (this._template.querySelectorAll('.single-row').length > 0) {
            console.log('query selector, number: ', this._template.querySelectorAll('.single-row').length);
            this._template.querySelectorAll('.single-row').forEach(function (el) {
              el.style.setAttribute('background-color', tooltipStyle.backgroundColor);
              el.style.setAttribute('color', tooltipStyle.color);
            });
          }
        }
        else {
          this._template.style = `background-color: ${tooltipStyle.backgroundColor}; color: ${tooltipStyle.color}; border-color: ${tooltipStyle.backgroundColor};`;
          if (this._template.querySelectorAll('.single-row').length > 0) {
            this._template.querySelectorAll('.single-row').forEach(function (el) {
              el.style = `background-color: ${tooltipStyle.backgroundColor}; color: ${tooltipStyle.color};`;
            });
          }
        }
      }
    }

    this._template.appendChild(body);
    this.setPosition();
    this.appendToBody
      ? window.document.body.insertAdjacentElement('beforeend', this._template)
      : this.element.nativeElement.insertAdjacentElement('afterbegin', this._template);

    if (this.maxTooltipWidth) {
      this._template.style.maxWidth = `${this.maxTooltipWidth}px`;
    }
    if (this.fixedTooltipWidth) {
      this._template.style.width = `${this.fixedTooltipWidth}px`;
    }
    if (this.minTooltipHeight) {
      this._template.style.minHeight = `${this.minTooltipHeight}px`;
    }
    if (this.maxTooltipHeight) {
      this._template.style.maxHeight = `${this.maxTooltipHeight}px`;
      body.style.maxHeight = `calc(${this.maxTooltipHeight}px - 1rem)`;
      body.style.lineHeight = `calc(${(this.maxTooltipHeight) / this.maxNumberOfLinesInTooltip}px - ${1 / this.maxNumberOfLinesInTooltip}rem)`;
      if (
        (this.usingChrome && (body.clientHeight < body.scrollHeight)) ||
        (this.usingIE && (body.clientHeight < (body.scrollHeight - 1)))
      ) {
        body.classList.add('ellipsis');
        if (this.usingIE) {
          body.classList.add('ellipsisIE');
        } else {
          body.style.setProperty('--ellipsis-right', `0.5rem`);
          body.style.setProperty('--ellipsis-bottom', `calc(${((this.maxTooltipHeight) / this.maxNumberOfLinesInTooltip) / 2}px - ${(1 / this.maxNumberOfLinesInTooltip) / 2}rem)`);
        }
      }
    }
    if (this.updateMaxToolTipWidthRelatively) {
      this._template.style.maxWidth = `${$event.x < window.innerWidth - $event.x ? $event.x * 2 : (window.innerWidth - $event.x) * 2}px`;
    }
    if (!!this.fontsize) {
      this._template.style.fontSize = this.fontsize;
    }
    this.forceUpdate = false;
  }

  private setPosition(previousPosition?) {
    try {
      let tooltipPositionString;
      if (typeof this.tooltipPosition === 'string') {
        tooltipPositionString = this.tooltipPosition;
        this.tooltipPosition = tooltipPositions[(<string> this.tooltipPosition).toUpperCase()];
      } else {
        tooltipPositionString = tooltipPositions[this.tooltipPosition];
      }
      this.updatePositionClass(tooltipPositionString, previousPosition);
    } catch (e) {
      console.error(e);
      this.tooltipPosition = tooltipPositions[tooltipPositions.LEFT];
    }

  }

  private updatePositionClass(tooltipPositionString, previousPosition?) {
    const arrowElement = this._template.querySelector('.tooltip-arrow');
    if (previousPosition) {
      previousPosition = typeof previousPosition === 'string' ? previousPosition : tooltipPositions[previousPosition];
      arrowElement.classList.remove('tooltip-arrow-' + previousPosition.toLowerCase());
      const style = [tooltipPositions.RIGHT, tooltipPositions.LEFT].indexOf(<tooltipPositions> this.tooltipPosition) > -1
        ? 'top: ' + Math.abs(this._template.getBoundingClientRect().top - this.arrowPosition.y) + 'px'
        : 'left:' + Math.abs(this._template.getBoundingClientRect().left - this.arrowPosition.x) + 'px';
      arrowElement.setAttribute('style', style);
    }

    arrowElement.classList.add('tooltip-arrow-' + tooltipPositionString.toLowerCase());
    if (this.usingIE) {
      arrowElement.classList.add('tooltip-arrow-ie');
    }
  }

  private addTooltip($event) {
    this.setTemplate($event);
    let heightOffset = this._template.offsetHeight - this.element.nativeElement.offsetHeight;
    heightOffset = heightOffset > 0 ? heightOffset / 2 : 0;
    this.setCoordinates(heightOffset);
  }

  private resetPosition(position, newPosition, heightOffset = 0) {
    let previousPosition;
    if (position < 0) {
      previousPosition = tooltipPositions[this.tooltipPosition];
      this.tooltipPosition = newPosition;
      this.setPosition(previousPosition);
      this.setCoordinates(heightOffset, true);
      return true;
    }
    return false;
  }

  private setCoordinates(heightOffset = 0, stopRecursion = false) {
    let position, parallelPosition;

    let nativeElementRect: any;
    if (this.useElementOffsetPos) {
      nativeElementRect = {
        top: this.element.nativeElement.offsetTop,
        bottom: this.element.nativeElement.offsetTop + this.element.nativeElement.offsetHeight,
        left: this.element.nativeElement.offsetLeft,
        right: this.element.nativeElement.offsetLeft + this.element.nativeElement.offsetWidth
      };
    } else {
      nativeElementRect = this.element.nativeElement.getBoundingClientRect();
    }

    switch (<tooltipPositions> this.tooltipPosition) {
      case tooltipPositions.RIGHT:
        position = (nativeElementRect.right + TooltipDirective.ARROW_OFFSET_PX);
        if (!stopRecursion && this.resetPosition(window.innerWidth - position, 'left', heightOffset)) {
          return;
        }
        this._template.style.left = position + 'px';
        if (this.tooltipAdditionalData) {
          this._template.style.top = (this.element.nativeElement.offsetHeight - heightOffset) + this.getTooltipYOffset() + 'px';
        } else {
          this._template.style.top = (nativeElementRect.top - heightOffset) + this.getTooltipYOffset() + 'px';
        }
        break;
      case tooltipPositions.BOTTOM:
        position = (nativeElementRect.bottom + TooltipDirective.ARROW_OFFSET_PX);
        if (!stopRecursion && this.resetPosition(window.innerHeight - position, 'top', heightOffset)) {
          return;
        }

        if (this.usingIE) {
          // For the IE we will calculate the left style (centered) of the tooltip arrow which has an absolute position style - GEN-140020
          // An automatic tooltip arrow centering doesn't work well for IE11 even with -ms specific styling
          this._template.children[0].style.left = this._template.getBoundingClientRect().width / 2 - this._template.children[0].getBoundingClientRect().width / 2 + 'px';
        }

        this._template.style.top = position + this.getTooltipYOffset() + 'px';
        parallelPosition = (nativeElementRect.left + (this.element.nativeElement.offsetWidth / 2) - (this._template.offsetWidth / 2));

        this.setParallelPosition(parallelPosition, 'left', 'BOTTOM');
        break;
      case tooltipPositions.LEFT:
        position = (nativeElementRect.left - this._template.offsetWidth - TooltipDirective.ARROW_OFFSET_PX);
        if (this.resetPosition(position, 'right', heightOffset)) {
          return;
        }
        this._template.style.left = (nativeElementRect.left - this._template.offsetWidth - TooltipDirective.ARROW_OFFSET_PX) + 'px';
        this._template.style.top = (nativeElementRect.top - heightOffset) + this.getTooltipYOffset() + 'px';
        break;
      case tooltipPositions.TOP:
        position = (nativeElementRect.top - this._template.getBoundingClientRect().height - TooltipDirective.ARROW_OFFSET_PX);
        if (this.resetPosition(position, 'bottom', heightOffset)) {
          return;
        }
        this._template.style.top = position + this.getTooltipYOffset() + 'px';
        parallelPosition = (nativeElementRect.left
          + (this.element.nativeElement.offsetWidth / 2) - (this._template.offsetWidth / 2));
        this.setParallelPosition(parallelPosition, 'left', 'TOP');
        break;
    }
  }

  private setParallelPosition(parallelPosition: number, tooltipAnchor: string, tooltipPositionString: string) {
    if (parallelPosition >= 0) {
      this._template.style[tooltipAnchor.toLowerCase()] = parallelPosition + 'px';
    } else {
      this._template.style[tooltipAnchor.toLowerCase()] = '0px';
      this.updatePositionClass(tooltipPositionString, tooltipPositionString);
    }
  }

  private getTooltipYOffset() {
    return (this.tooltipYOffset ? +(this.tooltipYOffset) : 0);
  }

  private removeTooltip() {
    try {
      this._template.innerHTML = '';
    } catch (e) {
    }
    this.templateInit = false;
  }

  ngAfterContentChecked(): void {
    if (!(this.tooltipConfiguration && this.tooltipConfiguration.componentBasedTooltip)) {
      if (!(this._tooltipData) || !(this._tooltipConfiguration)) {
        if (this.tooltipAdditionalData) {
          this._tooltipService.getTooltipContent(this._tooltipData, this._tooltipConfiguration, this.entryRef);
        } else {
          return;
        }
      }
      this._tooltipService.getTooltipContent(this._tooltipData, this._tooltipConfiguration, this.entryRef);
    }
  }

  setTooltipText() {
    if (this.tooltipData && this.tooltipConfiguration) {
      if (typeof (this.tooltipData) === 'string') {
        this.tooltipText = this.tooltipData;
      } else if (
        get(this.tooltipConfiguration, 'tooltipDisplayPattern') && typeof (get(this.tooltipConfiguration, 'tooltipDisplayPattern')) === 'string') {
        // Filtering for data inside the specific path
        const filteredData = (get(this.tooltipConfiguration, 'pathToData') !== '*') ? get(this.tooltipData, this.tooltipConfiguration.pathToData) : this.tooltipData;
        // Interpolating with form Data if any interpolation strings are there
        if (InterpolateService.hasInterpolationString(<string> this.tooltipConfiguration.tooltipDisplayPattern)) {
          this.tooltipText =  InterpolateService.interpolate(<string> this.tooltipConfiguration.tooltipDisplayPattern, filteredData , true);
          this.tooltipText = this.tooltipText.replace(/ \- \[object Object]/gi, ''); // Removing empty object strings in case object is null and removing the last '-'
          this.tooltipText = this.tooltipText.replace(/\[object Object]/gi, ''); // Removing empty object strings in case object is null
        }
      }
    }
  }

  private preChecks() {
    return this.tooltipConfiguration &&
      this.tooltipConfiguration.componentBasedTooltip &&
      (this.tooltipShow || this.tooltipConfiguration.tooltipShow) &&
      (this.tooltipText || this.tooltipConfiguration.payload || this.tooltipHtml);
  }
}

export enum tooltipPositions {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  TOP = 'TOP',
  BOTTOM = 'BOTTOM'
}
